import styled, { css, keyframes } from "styled-components"

const WHATSAPP = "#25D366"
const WHATSAPP_DARK = "#128C7E"

const enter = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`

const ring = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.35); }
  70% { box-shadow: 0 0 0 12px rgba(37, 211, 102, 0); }
  100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); }
`

export const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background-color: #f1f5f9;
    color: #334155;
  }
`

const bodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${enter} 0.22s ease-out;
`

export const Body = styled.div`
  ${bodyStyles}
`

export const BodyForm = styled.form`
  ${bodyStyles}
`

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 4px 8px 0;
`

export const IconBadge = styled.div`
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${WHATSAPP} 0%, ${WHATSAPP_DARK} 100%);
  margin-bottom: 6px;
  animation: ${ring} 2.2s ease-out infinite;

  svg {
    width: 26px;
    height: 26px;
    color: #fff;
  }
`

export const Title = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  letter-spacing: -0.01em;
`

export const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
  max-width: 320px;
`

export const PhoneCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f0fdf4;
  border: 1.5px solid rgba(37, 211, 102, 0.45);
`

export const PhoneCardIcon = styled.div`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #fff;
  color: ${WHATSAPP_DARK};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);

  svg {
    width: 22px;
    height: 22px;
  }
`

export const PhoneCardText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const PhoneCardLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${WHATSAPP_DARK};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

export const PhoneNumber = styled.span`
  /* Cabe em celular de 360px sem vazar do cartão. */
  font-size: clamp(17px, 5vw, 20px);
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #334155;
`

export const InputWrapper = styled.div<{ $invalid: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 16px;
  border-radius: 14px;
  background: #fff;
  border: 1.5px solid ${({ $invalid }) => ($invalid ? "#ef4444" : "#cbd5e1")};
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: ${({ $invalid }) => ($invalid ? "#ef4444" : WHATSAPP)};
    box-shadow: 0 0 0 4px
      ${({ $invalid }) =>
        $invalid ? "rgba(239, 68, 68, 0.12)" : "rgba(37, 211, 102, 0.18)"};
  }

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: ${WHATSAPP_DARK};
  }
`

export const PhoneInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;

  &::placeholder {
    color: #cbd5e1;
    font-weight: 500;
  }
`

export const ErrorText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #dc2626;
`

export const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
`

export const Button = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: 0.75rem 1.4rem;
  border: none;
  border-radius: 14px;
  outline: none;
  background: ${({ $disabled, theme }) =>
    $disabled ? "#cbd5e1" : theme.colors.blue.main};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  transition: box-shadow 0.2s, transform 0.1s, background-color 0.2s;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    box-shadow: ${({ $disabled }) =>
      $disabled ? "none" : "0 6px 16px rgba(0, 97, 230, 0.28)"};
  }

  &:active {
    transform: ${({ $disabled }) => ($disabled ? "none" : "scale(0.99)")};
  }

  &:focus-visible {
    box-shadow: 0 0 0 4px rgba(0, 97, 230, 0.25);
  }
`

export const LinkButton = styled.button`
  align-self: center;
  padding: 10px 12px;
  border: none;
  border-radius: 10px;
  outline: none;
  background: transparent;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: #0f172a;
    background: #f1f5f9;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(0, 97, 230, 0.2);
  }
`
